import type { IWrappedProperty } from '@witkac/jschema';
import type { ComponentModel } from '@witkac/vue3-components/src/typings/componentModel';
import { computed, type ComputedRef, getCurrentInstance, ref, type Ref, shallowRef } from 'vue';

import { confirmService } from '../services/confirm.service';
import type { Tab } from '../typings/tab';

export function useTabs<T extends IWrappedProperty>(schema: T, tabs: Tab[]): UseTabsReturnType {
  const instance = getCurrentInstance();
  const tabsRef = ref(isTabDisabled());
  const selectedTab = ref(tabsRef.value[0]);
  const currentTabFields = shallowRef<ComponentModel[]>([]);

  function setCurrentTabFields(fields: ComponentModel[]): void {
    currentTabFields.value = [...fields];
  }

  function isTabDisabled() {
    if (!schema.$external.tabsEnabled.length) {
      return tabs;
    }

    const unlockInfo = schema.$external.$settings.unlockInfo;

    return tabs.map((tab, index) => {
      tab.disabled = !schema.$external.tabsEnabled.includes(index);
      return { ...tab, unlockInfo };
    });
  }

  function nextTab() {
    const index = tabsRef.value.findIndex((tab) => tab === selectedTab.value);
    if (index < 0 || index + 1 >= tabsRef.value.length) {
      return;
    }

    setTab(tabsRef.value[index + 1]);
  }

  function prevTab() {
    const index = tabsRef.value.findIndex((tab) => tab === selectedTab.value);

    if (index < 0 || index - 1 < 0) {
      return;
    }

    setTab(tabsRef.value[index - 1]);
  }

  function validateCurrentTab(): IWrappedProperty[] {
    const result: any[] = [];

    currentTabFields.value.forEach((field) => {
      const validationResult = field?.$validate();
      if (validationResult) {
        result.push(validationResult);
      }
    });

    return result;
  }

  function tabChanged(tab: Tab) {
    selectedTab.value = tab;
    schema.$readOnly = tab.disabled;
    instance?.emit('tab-changed', tab);
  }

  function setTab(tab: Tab) {
    if (tab === selectedTab.value) {
      return;
    }
    const errors = validateCurrentTab();
    instance?.emit('validate', errors);

    if (errors.length === 0) {
      tabChanged(tab);
      return;
    }

    return confirmService
      .confirm(
        'Nieprawidłowo wypełniono pola w tej części wniosku. Czy chcesz przejść dalej, a błędy poprawić później?',
      )
      .then(() => tabChanged(tab))
      .then(() => window.scrollTo(0, 0));
  }

  return {
    isFirstIndex: computed(() => tabsRef.value[0] === selectedTab.value),
    isLastIndex: computed(() => tabsRef.value[tabsRef.value.length - 1] === selectedTab.value),
    tabs: tabsRef,
    selectedTab,

    setCurrentTabFields,
    nextTab,
    prevTab,
    setTab,
  };
}

export type UseTabsReturnType = {
  isFirstIndex: ComputedRef<boolean>;
  isLastIndex: ComputedRef<boolean>;
  tabs: Ref<Tab[]>;
  selectedTab: Ref<Tab>;
  setCurrentTabFields: (fields: ComponentModel[]) => void;
  nextTab: () => any;
  prevTab: () => any;
  setTab: (tab: Tab) => any;
};
